import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PrivacyPolicyContent } from "@/components/PrivacyPolicyContent";
import { useNavigate } from 'react-router-dom';
import { SAFE_AREA } from "@/utils/safeArea";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  User, 
  Target, 
  Dumbbell, 
  BarChart3, 
  ArrowRight, 
  ArrowLeft,
  Zap,
  Calendar,
  Weight,
  Ruler,
  Plus,
  Trash2,
  Loader2,
  Activity,
  Watch,
  CheckCircle2
} from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { useGoals, Goal } from '@/hooks/useGoals';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Analytics } from '@/lib/analytics';
import { Capacitor } from '@capacitor/core';
import { getHealthConnectedState, presentHealthScreenNative } from '@/lib/appleHealth';
import { useKeyboardHeight } from '@/hooks/useKeyboardHeight';
import { registerPlugin } from '@capacitor/core';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/env';

import straivLogoDark from "@/assets/straiv-logo-dark.png";
import loadingGif from "@/assets/loading.gif";

export const Onboarding = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile, completeOnboarding, refetch: refetchProfile } = useProfile();
  const { user } = useAuth();
  const { createGoal } = useGoals();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [onboardingGoals, setOnboardingGoals] = useState<Omit<Goal, 'id' | 'created_at' | 'updated_at'>[]>([]);
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [newGoal, setNewGoal] = useState({
    goal_type: 'strength',
    exercise_name: '',
    target_value: 0,
    target_unit: 'kg',
    current_value: 0,
    starting_value: null as number | null,
    target_date: '',
    is_active: true,
    description: ''
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
    fitness_level: '',
    primary_goals: [] as string[],
    experience_years: null as number | null,
    height_cm: '',
    weight_kg: '',
    birth_date: '',
    work_type: '',
    work_type_custom: '',
    health_deficiencies: '',
    nutrition_restrictions: ''
  });
  const [dataConsentAccepted, setDataConsentAccepted] = useState(false);
  const [isSocialSignIn, setIsSocialSignIn] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [healthConnected, setHealthConnected] = useState(false);
  const [isConnectingHealth, setIsConnectingHealth] = useState(false);

  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const keyboardHeight = useKeyboardHeight();

  // Check Health connection when entering step 6 (iOS) - per-user so new accounts don't inherit
  useEffect(() => {
    if (currentStep === 6 && isIOS && user?.id) {
      getHealthConnectedState(user.id).then(setHealthConnected);
    }
  }, [currentStep, isIOS, user?.id]);

  const handleConnectAppleHealth = async () => {
    if (!isIOS || !user) return;
    setIsConnectingHealth(true);
    try {
      // Set credentials for sync (user is logged in during onboarding)
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token && session.user.id) {
        const plugin = registerPlugin<{ setHealthSyncCredentials: (opts: { accessToken: string; userId: string; supabaseUrl?: string; supabaseAnonKey?: string }) => Promise<void> }>('StraivHealthConnect');
        try {
          await plugin.setHealthSyncCredentials({
            accessToken: session.access_token,
            userId: session.user.id,
            supabaseUrl: SUPABASE_URL,
            supabaseAnonKey: SUPABASE_ANON_KEY,
          });
        } catch {
          // Ignore - may already be set
        }
      }
      const connected = await presentHealthScreenNative({ syncOnDismiss: true, userId: user.id, skipFeedback: true });
      setHealthConnected(connected);
      if (connected) {
        toast.success(t('onboarding.toasts.appleHealthConnected'));
      }
    } finally {
      setIsConnectingHealth(false);
    }
  };

  // Helper: Check if a name looks like a real name (not email-derived or random)
  const isValidRealName = (name: string | null | undefined, email: string | null | undefined): boolean => {
    if (!name || name.trim().length === 0) return false;
    
    const trimmed = name.trim();
    const emailPrefix = email?.split('@')[0]?.toLowerCase() || '';
    const lowerName = trimmed.toLowerCase();
    
    // Reject if it matches email prefix
    if (emailPrefix && lowerName === emailPrefix) {
      console.log(`Rejecting "${name}" - matches email prefix`);
      return false;
    }
    
    // Reject if it contains the email prefix (partial match)
    if (emailPrefix && emailPrefix.length > 3 && lowerName.includes(emailPrefix)) {
      console.log(`Rejecting "${name}" - contains email prefix`);
      return false;
    }
    
    // Reject if it contains numbers
    if (/\d/.test(trimmed)) {
      console.log(`Rejecting "${name}" - contains numbers`);
      return false;
    }
    
    // Reject if it's longer than 15 chars with no spaces (likely username)
    if (trimmed.length > 15 && !/\s/.test(trimmed)) {
      console.log(`Rejecting "${name}" - too long with no spaces`);
      return false;
    }
    
    // Reject if it looks like a random string (no vowels)
    const hasVowels = /[aeiouAEIOU]/.test(trimmed);
    if (!hasVowels && trimmed.length > 3) {
      console.log(`Rejecting "${name}" - no vowels (random string)`);
      return false;
    }
    
    console.log(`Accepting "${name}" as valid name`);
    return true;
  };

  // Track onboarding started
  useEffect(() => {
    Analytics.onboardingStarted();
  }, []);

  // Pre-populate form data from social sign-in (Apple/Google)
  useEffect(() => {
    const populateNameFromAuth = async () => {
      try {
        console.log('=== ONBOARDING NAME POPULATION ===');
        
        const { data: { user } } = await supabase.auth.getUser();
        const userEmail = user?.email || '';
        const provider = user?.app_metadata?.provider;
        
        // Track if this is a social sign-in (Apple/Google)
        const socialProviders = ['apple', 'google'];
        if (provider && socialProviders.includes(provider)) {
          setIsSocialSignIn(true);
          console.log('Social sign-in detected:', provider);
        }
        
        // FIRST: Check sessionStorage for names from social sign-in
        // This is set immediately during Apple/Google sign-in to avoid race conditions
        const storedFirstName = sessionStorage.getItem('social_signin_first_name');
        const storedLastName = sessionStorage.getItem('social_signin_last_name');
        
        console.log('SessionStorage firstName:', storedFirstName);
        console.log('SessionStorage lastName:', storedLastName);
        
        // Validate sessionStorage names
        if (storedFirstName && isValidRealName(storedFirstName, userEmail)) {
          console.log('Using sessionStorage names:', storedFirstName, storedLastName);
          setFormData(prev => ({
            ...prev,
            first_name: storedFirstName,
            last_name: isValidRealName(storedLastName, userEmail) ? storedLastName || '' : '',
          }));
          // Clear sessionStorage after reading
          sessionStorage.removeItem('social_signin_first_name');
          sessionStorage.removeItem('social_signin_last_name');
          console.log('=================================');
          return;
        }
        
        // Clear any invalid sessionStorage data
        sessionStorage.removeItem('social_signin_first_name');
        sessionStorage.removeItem('social_signin_last_name');
        
        // SECOND: Check profile (for returning users)
        if (profile?.first_name && isValidRealName(profile.first_name, userEmail)) {
          console.log('Using profile name:', profile.first_name, profile.last_name);
          setFormData(prev => ({
            ...prev,
            first_name: profile.first_name || '',
            last_name: isValidRealName(profile.last_name, userEmail) ? profile.last_name || '' : '',
          }));
          console.log('=================================');
          return;
        }
        
        // THIRD: Check Supabase user metadata (for web OAuth)
        console.log('User email:', userEmail);
        console.log('User metadata:', JSON.stringify(user?.user_metadata));
        
        if (user?.user_metadata) {
          const metadata = user.user_metadata;
          let firstName = metadata.given_name || metadata.first_name || null;
          let lastName = metadata.family_name || metadata.last_name || null;
          
          // Try to split full_name if no valid first/last
          if ((!firstName || !isValidRealName(firstName, userEmail)) && (metadata.full_name || metadata.name)) {
            const fullName = metadata.full_name || metadata.name;
            if (fullName && fullName.includes(' ') && isValidRealName(fullName, userEmail)) {
              const nameParts = fullName.trim().split(' ');
              firstName = nameParts[0] || null;
              lastName = nameParts.slice(1).join(' ') || null;
            }
          }
          
          console.log('Extracted from metadata - firstName:', firstName, 'lastName:', lastName);
          
          // Only populate if names pass validation
          if (firstName && isValidRealName(firstName, userEmail)) {
            setFormData(prev => ({
              ...prev,
              first_name: firstName,
              last_name: isValidRealName(lastName, userEmail) ? lastName || '' : '',
            }));
          } else {
            console.log('No valid name found, leaving fields empty for user to enter');
          }
        }
        console.log('=================================');
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    populateNameFromAuth();
  }, [profile]);

  const totalSteps = isIOS ? 8 : 7;
  const progress = (currentStep / totalSteps) * 100;

  const fitnessLevels = [
    { value: 'beginner', label: t('onboarding.step2.levels.beginner.label'), desc: t('onboarding.step2.levels.beginner.desc') },
    { value: 'intermediate', label: t('onboarding.step2.levels.intermediate.label'), desc: t('onboarding.step2.levels.intermediate.desc') },
    { value: 'advanced', label: t('onboarding.step2.levels.advanced.label'), desc: t('onboarding.step2.levels.advanced.desc') },
    { value: 'elite', label: t('onboarding.step2.levels.elite.label'), desc: t('onboarding.step2.levels.elite.desc') }
  ];

  // Internal IDs are the English keys; display label is translated via t('onboarding.step3.goals.<id>')
  const primaryGoals = [
    'Build Muscle',
    'Lose Weight',
    'Improve Strength',
    'Increase Endurance',
    'Better Health',
    'Sport Performance',
    'Flexibility',
    'Mental Wellness'
  ];

  const handleGoalToggle = (goal: string) => {
    setFormData(prev => ({
      ...prev,
      primary_goals: prev.primary_goals.includes(goal)
        ? prev.primary_goals.filter(g => g !== goal)
        : [...prev.primary_goals, goal]
    }));
    // Clear error when user selects a goal
    if (validationErrors.primary_goals) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next.primary_goals;
        return next;
      });
    }
  };

  const stepNames = isIOS
    ? ['Basic Info', 'Fitness Level', 'Overall Goals', 'Specific Goals', 'Work Type', 'Connect Apple Health', 'Health Conditions', 'Dietary Restrictions']
    : ['Basic Info', 'Fitness Level', 'Overall Goals', 'Specific Goals', 'Work Type', 'Health Conditions', 'Dietary Restrictions'];
  
  const handleNext = () => {
    if (currentStep < totalSteps) {
      // If leaving Step 4 (Specific Goals) with an in-progress goal, auto-add it
      if (currentStep === 4 && showGoalDialog && newGoal.exercise_name && newGoal.target_value > 0) {
        setOnboardingGoals(prev => [...prev, { ...newGoal, starting_value: newGoal.current_value, user_id: '' }]);
        setNewGoal({
          goal_type: 'strength',
          exercise_name: '',
          target_value: 0,
          target_unit: 'kg',
          current_value: 0,
          starting_value: null,
          target_date: '',
          is_active: true,
          description: ''
        });
        setShowGoalDialog(false);
      }
      // Track step completion
      Analytics.onboardingStepCompleted(currentStep, stepNames[currentStep - 1] || `Step ${currentStep}`);
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleAddGoal = () => {
    if (newGoal.exercise_name && newGoal.target_value > 0) {
      setOnboardingGoals(prev => [...prev, { ...newGoal, starting_value: newGoal.current_value, user_id: '' }]);
      setNewGoal({
        goal_type: 'strength',
        exercise_name: '',
        target_value: 0,
        target_unit: 'kg',
        current_value: 0,
        starting_value: null,
        target_date: '',
        is_active: true,
        description: ''
      });
      setShowGoalDialog(false);
      toast.success(t('onboarding.toasts.goalAdded'));
    }
  };

  const handleRemoveGoal = (index: number) => {
    setOnboardingGoals(prev => prev.filter((_, i) => i !== index));
  };

  // Validation function with detailed error messages
  const validateForm = (): { isValid: boolean; errors: string[]; fieldErrors: Record<string, string> } => {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    // Step 1: Basic Info - Gender is required
    if (!formData.gender || formData.gender.trim() === '') {
      errors.push(t('onboarding.validation.selectGender'));
      fieldErrors.gender = t('onboarding.validation.genderRequired');
    }

    if (!formData.fitness_level || formData.fitness_level.trim() === '') {
      errors.push(t('onboarding.validation.selectFitnessLevel'));
      fieldErrors.fitness_level = t('onboarding.validation.fitnessLevelRequired');
    }

    if (!formData.primary_goals || formData.primary_goals.length === 0) {
      errors.push(t('onboarding.validation.selectPrimaryGoal'));
      fieldErrors.primary_goals = t('onboarding.validation.primaryGoalRequired');
    }

    if (!formData.work_type || formData.work_type.trim() === '') {
      errors.push(t('onboarding.validation.selectWorkType'));
      fieldErrors.work_type = t('onboarding.validation.workTypeRequired');
    }
    if (formData.work_type === 'other' && (!formData.work_type_custom || formData.work_type_custom.trim() === '')) {
      errors.push(t('onboarding.validation.specifyWorkType'));
      fieldErrors.work_type_custom = t('onboarding.validation.specifyWorkType');
    }

    if (!dataConsentAccepted) {
      errors.push(t('onboarding.validation.acceptDataConsent'));
      fieldErrors.dataConsent = t('onboarding.validation.dataConsentRequired');
    }

    if (formData.birth_date) {
      const birthDate = new Date(formData.birth_date);
      const today = new Date();
      if (isNaN(birthDate.getTime())) {
        errors.push(t('onboarding.validation.validBirthDate'));
        fieldErrors.birth_date = t('onboarding.validation.invalidDateFormat');
      } else if (birthDate > today) {
        errors.push(t('onboarding.validation.birthDateFuture'));
        fieldErrors.birth_date = t('onboarding.validation.birthDateFuture');
      } else {
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 13) {
          errors.push(t('onboarding.validation.minAge13'));
          fieldErrors.birth_date = t('onboarding.validation.minAge13Short');
        } else if (age > 120) {
          errors.push(t('onboarding.validation.validBirthDate'));
          fieldErrors.birth_date = t('onboarding.validation.validBirthDate');
        }
      }
    }

    if (formData.height_cm) {
      const height = parseInt(formData.height_cm);
      if (isNaN(height) || height < 50 || height > 250) {
        errors.push(t('onboarding.validation.validHeight'));
        fieldErrors.height_cm = t('onboarding.validation.heightRange');
      }
    }

    if (formData.weight_kg) {
      const weight = parseFloat(formData.weight_kg);
      if (isNaN(weight) || weight < 20 || weight > 500) {
        errors.push(t('onboarding.validation.validWeight'));
        fieldErrors.weight_kg = t('onboarding.validation.weightRange');
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      fieldErrors
    };
  };

  const handleSubmit = async () => {
    // Validate form before submission
    const validation = validateForm();
    if (!validation.isValid) {
      // Set field errors for inline display
      setValidationErrors(validation.fieldErrors);
      
      // Show first error as main toast, others as secondary
      toast.error(validation.errors[0] || t('onboarding.toasts.validationGeneric'), {
        description: validation.errors.length > 1
          ? t('onboarding.toasts.moreErrors', { count: validation.errors.length - 1 })
          : undefined
      });
      
      // Scroll to first error field if possible
      const firstErrorField = Object.keys(validation.fieldErrors)[0];
      if (firstErrorField) {
        const errorElement = document.getElementById(firstErrorField);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorElement.focus();
        }
      }
      return;
    }

    // Clear any previous validation errors
    setValidationErrors({});

    setIsSubmitting(true);
    let retryCount = 0;
    const maxRetries = 3;

    while (retryCount < maxRetries) {
      try {
        // Map work_type to activity_level
        let activity_level = 'moderate'; // default
        if (formData.work_type === 'student' || formData.work_type === 'office' || formData.work_type === 'remote') {
          activity_level = 'sedentary';
        } else if (formData.work_type === 'physical') {
          activity_level = 'active';
        }

        // Calculate age from birth_date
        let age = null;
        if (formData.birth_date) {
          const birthDate = new Date(formData.birth_date);
          const today = new Date();
          age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
        }

        // Create all onboarding goals (also include any in-progress goal not explicitly added)
        const goalsToCreate = [...onboardingGoals];
        if (newGoal.exercise_name && newGoal.target_value > 0) {
          goalsToCreate.push({ ...newGoal, starting_value: newGoal.current_value });
        }

        // Auto-create weight goal if "Lose Weight" is selected and weight is provided
        const currentWeight = formData.weight_kg ? parseFloat(formData.weight_kg) : null;
        if (formData.primary_goals.includes('Lose Weight') && currentWeight && currentWeight > 0) {
          // Calculate target weight (10% reduction or at least 5kg less)
          const targetWeight = Math.max(currentWeight * 0.9, currentWeight - 5);
          goalsToCreate.push({
            goal_type: 'weight',
            exercise_name: 'Body Weight',
            target_value: Math.round(targetWeight * 10) / 10,
            target_unit: 'kg',
            current_value: currentWeight,
            starting_value: currentWeight,
            target_date: null,
            is_active: true,
            description: 'Weight loss goal from onboarding'
          } as any);
        }

        // Format specific goals as text for profile.specific_goals
        const specificGoalsText = goalsToCreate
          .filter(goal => goal.exercise_name && goal.target_value > 0)
          .map(goal => {
            const exerciseName = goal.exercise_name || '';
            const targetValue = goal.target_value || 0;
            const unit = goal.target_unit || '';
            return `${exerciseName} ${targetValue}${unit}`;
          })
          .join(', ');

        const profileData = {
          ...formData,
          activity_level,
          age,
          height_cm: formData.height_cm ? parseInt(formData.height_cm) : null,
          weight_kg: formData.weight_kg ? parseFloat(formData.weight_kg) : null,
          birth_date: formData.birth_date || null,
          tutorial_completed: false,
          specific_goals: specificGoalsText || null,
        };

        // Attempt to complete onboarding with retry logic
        const { error } = await completeOnboarding(profileData);
        
        if (error) {
          retryCount++;
          if (retryCount < maxRetries) {
            // Wait before retry with exponential backoff
            const delay = Math.pow(2, retryCount) * 500; // 1s, 2s, 4s
            await new Promise(resolve => setTimeout(resolve, delay));
            continue; // Retry
          } else {
            // All retries failed
            console.error('[Onboarding] Failed after retries:', error);
            toast.error(t('onboarding.toasts.saveProfileFailed'), {
              description: error.message || t('onboarding.toasts.saveProfileFailedDesc')
            });
            Analytics.errorOccurred('onboarding_database_error', 'onboarding');
            setIsSubmitting(false);
            return;
          }
        }

        // Successfully saved to database - verify by refetching profile
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait for database to update
        await refetchProfile();
        
        // Double-check profile was updated (non-blocking - don't fail if verification fails)
        if (user) {
          const { data: verifyProfile } = await supabase
            .from('profiles')
            .select('onboarding_completed')
            .eq('user_id', user.id)
            .maybeSingle();
          
          if (verifyProfile && !verifyProfile.onboarding_completed) {
            console.warn('[Onboarding] Profile verification failed, but continuing...');
            // Try one more time
            const { error: retryError } = await completeOnboarding(profileData);
            if (retryError) {
              throw new Error('Profile verification failed after retry');
            }
          }
        }

        // Create goals (non-blocking - don't fail onboarding if goals fail)
        for (const goal of goalsToCreate) {
          try {
            await createGoal(goal);
          } catch (gErr) {
            console.error('Failed to create goal:', gErr);
            // Don't block onboarding completion for goal creation failures
          }
        }

        // Mark onboarding done on client to avoid immediate redirect loops
        localStorage.setItem('onboarding_completed', 'true');
        // Ensure tutorial shows next
        localStorage.setItem('tutorial_completed', 'false');
        
        // Track onboarding completion (non-blocking - wrapped in try-catch to prevent failures)
        // Analytics should NEVER block onboarding completion
        try {
          Analytics.onboardingCompleted();
        } catch (analyticsError: any) {
          console.warn('[Onboarding] Analytics.onboardingCompleted error (non-critical):', analyticsError);
        }
        
        // Set user properties for segmentation (non-blocking - don't fail onboarding if this fails)
        try {
          if (formData.fitness_level) {
            await Analytics.setUserProperty('fitness_level', formData.fitness_level);
          }
          if (formData.primary_goals.length > 0) {
            await Analytics.setUserProperty('primary_goal', formData.primary_goals[0]);
          }
        } catch (analyticsError: any) {
          // Log but don't fail onboarding if analytics fails
          console.warn('[Onboarding] Analytics.setUserProperty error (non-critical):', analyticsError);
        }
        
        // Success - navigate to next step
        toast.success(t('onboarding.toasts.profileCreated'), {
          description: t('onboarding.toasts.profileCreatedDesc')
        });
        
        // Add a slight delay to show the success message and ensure profile is updated
        setTimeout(() => {
          setIsSubmitting(false);
          navigate('/educational-slides', { replace: true });
        }, 1000);
        return; // Success - exit retry loop

      } catch (error: any) {
        retryCount++;
        if (retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 500;
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        } else {
          console.error('[Onboarding] Submit error:', error);
          toast.error(t('onboarding.toasts.somethingWentWrong'), {
            description: error.message || t('onboarding.toasts.somethingWentWrongDesc')
          });
          Analytics.errorOccurred('onboarding_failed', 'onboarding');
          setIsSubmitting(false);
          return;
        }
      }
    }
  };
  const canProceed = () => {
    // Step 1: Basic Info - Gender required
    if (currentStep === 1) {
      return formData.gender && formData.gender.trim() !== '';
    }
    // Step 2: Fitness Level - Required
    if (currentStep === 2) {
      return formData.fitness_level && formData.fitness_level.trim() !== '';
    }
    // Step 3: Primary Goals - At least one required
    if (currentStep === 3) {
      return formData.primary_goals && formData.primary_goals.length > 0;
    }
    // Step 5: Work Type - Required
    if (currentStep === 5) {
      if (!formData.work_type || formData.work_type.trim() === '') return false;
      if (formData.work_type === 'other' && (!formData.work_type_custom || formData.work_type_custom.trim() === '')) {
        return false;
      }
      return true;
    }
    // Step 6: Connect Apple Health (iOS only) - Optional
    if (currentStep === 6 && isIOS) return true;
    // Last step: Data Consent - Required (step 8 for iOS, step 7 for non-iOS)
    if (currentStep === totalSteps) {
      return dataConsentAccepted;
    }
    // Steps 4, 6 (non-iOS Health Conditions), 7 (iOS Health Conditions) are optional
    return true;
  };

  return (
    <div 
      className="min-h-screen bg-gradient-bg flex items-center justify-center p-4 pb-safe" 
      style={{ 
        paddingTop: `max(${SAFE_AREA.top}, 16px)`,
        paddingBottom: `max(${SAFE_AREA.bottom}, 16px)`,
      }}
    >
      {isSubmitting && (
        <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-6 animate-fade-in">
          <img 
            src={loadingGif} 
            alt="Loading" 
            className="h-32 w-32 object-contain"
          />
          <div className="text-center space-y-2 animate-fade-in">
            <h3 className="text-2xl font-bold text-foreground">{t('onboarding.creatingProfile')}</h3>
            <p className="text-muted-foreground">{t('onboarding.settingUp')}</p>
          </div>
        </div>
      )}
      <Card 
        className="w-full max-w-md bg-gradient-card border-border/50 shadow-card flex flex-col"
        style={{ maxHeight: `calc(100vh - max(${SAFE_AREA.top}, 32px) - max(${SAFE_AREA.bottom}, 32px) - 32px)` }}
      >
        <CardHeader className="text-center space-y-3 flex-shrink-0 pb-3">
          <div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-xl font-bold">{t('onboarding.welcomeTo')}</span>
              <img 
                src={straivLogoDark}
                alt="STRAIV" 
                className="h-28 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-muted-foreground">{t('onboarding.subtitle')}</p>
          </div>
          <div className="space-y-1">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground">{t('onboarding.stepLabel', { current: currentStep, total: totalSteps })}</p>
          </div>
        </CardHeader>

        <CardContent 
          className="space-y-4 overflow-y-auto flex-1"
          style={{ 
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            paddingBottom: keyboardHeight > 0 ? `calc(24px + ${keyboardHeight}px)` : 160,
          }}
        >
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{t('onboarding.step1.title')}</h3>
              </div>

              {isSocialSignIn && (
                <p className="text-xs text-muted-foreground -mt-2">
                  {t('onboarding.step1.socialNote')}
                </p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">
                    {t('onboarding.step1.firstName')} {isSocialSignIn && <span className="text-muted-foreground font-normal">{t('onboarding.step1.optional')}</span>}
                  </Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    placeholder={isSocialSignIn ? t('onboarding.step1.optionalPlaceholder') : t('onboarding.step1.firstNamePlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">
                    {t('onboarding.step1.lastName')} {isSocialSignIn && <span className="text-muted-foreground font-normal">{t('onboarding.step1.optional')}</span>}
                  </Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    placeholder={isSocialSignIn ? t('onboarding.step1.optionalPlaceholder') : t('onboarding.step1.lastNamePlaceholder')}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">{t('onboarding.step1.gender')}</Label>
                  <Select 
                    value={formData.gender || ''} 
                    onValueChange={(value) => {
                      setFormData(prev => ({ ...prev, gender: value }));
                      // Clear error when user selects
                      if (validationErrors.gender) {
                        setValidationErrors(prev => {
                          const next = { ...prev };
                          delete next.gender;
                          return next;
                        });
                      }
                    }}
                  >
                    <SelectTrigger id="gender" className={validationErrors.gender ? 'border-destructive' : ''}>
                      <SelectValue placeholder={t('onboarding.step1.selectGender')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t('onboarding.step1.genderMale')}</SelectItem>
                      <SelectItem value="female">{t('onboarding.step1.genderFemale')}</SelectItem>
                      <SelectItem value="other">{t('onboarding.step1.genderOther')}</SelectItem>
                      <SelectItem value="prefer_not_to_say">{t('onboarding.step1.genderPreferNot')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {validationErrors.gender && (
                    <p className="text-sm text-destructive">{validationErrors.gender}</p>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('onboarding.step1.birthDate')}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {/* Year selector - most important for age */}
                      <Select
                        value={formData.birth_date ? formData.birth_date.split('-')[0] : ''}
                        onValueChange={(year) => {
                          const parts = formData.birth_date ? formData.birth_date.split('-') : ['', '01', '01'];
                          const newDate = `${year}-${parts[1] || '01'}-${parts[2] || '01'}`;
                          setFormData(prev => ({ ...prev, birth_date: newDate }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('onboarding.step1.year')} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {Array.from({ length: 100 }, (_, i) => {
                            const year = new Date().getFullYear() - 13 - i; // Start from 13 years ago
                            return (
                              <SelectItem key={year} value={year.toString()}>
                                {year}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      
                      {/* Month selector */}
                      <Select
                        value={formData.birth_date ? formData.birth_date.split('-')[1] : ''}
                        onValueChange={(month) => {
                          const parts = formData.birth_date ? formData.birth_date.split('-') : [new Date().getFullYear().toString(), '', '01'];
                          const newDate = `${parts[0]}-${month}-${parts[2] || '01'}`;
                          setFormData(prev => ({ ...prev, birth_date: newDate }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('onboarding.step1.month')} />
                        </SelectTrigger>
                        <SelectContent>
                          {['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].map((monthKey, i) => (
                            <SelectItem key={i} value={(i + 1).toString().padStart(2, '0')}>
                              {t(`onboarding.step1.months.${monthKey}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {/* Day selector */}
                      <Select
                        value={formData.birth_date ? formData.birth_date.split('-')[2] : ''}
                        onValueChange={(day) => {
                          const parts = formData.birth_date ? formData.birth_date.split('-') : [new Date().getFullYear().toString(), '01', ''];
                          const newDate = `${parts[0]}-${parts[1]}-${day}`;
                          setFormData(prev => ({ ...prev, birth_date: newDate }));
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('onboarding.step1.day')} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {Array.from({ length: 31 }, (_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString().padStart(2, '0')}>
                              {i + 1}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {validationErrors.birth_date && (
                      <p className="text-sm text-destructive">{validationErrors.birth_date}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="height_cm">{t('onboarding.step1.heightCm')}</Label>
                      <div className="relative">
                        <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          id="height_cm"
                          type="text"
                          inputMode="decimal"
                          value={formData.height_cm}
                          onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setFormData(prev => ({ ...prev, height_cm: value }));
                            }
                          }}
                          onFocus={(e) => e.target.select()}
                          placeholder="170"
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="weight_kg">{t('onboarding.step1.weightKg')}</Label>
                      <div className="relative">
                        <Weight className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          id="weight_kg"
                          type="text"
                          inputMode="decimal"
                          value={formData.weight_kg}
                          onChange={(e) => {
                            const value = e.target.value.replace(',', '.');
                            if (value === '' || /^\d*\.?\d*$/.test(value)) {
                              setFormData(prev => ({ ...prev, weight_kg: value }));
                            }
                          }}
                          onFocus={(e) => {
                            e.target.select();
                            setTimeout(() => {
                              (e.target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 350);
                          }}
                          placeholder="70"
                          className="pl-10"
                          style={{ scrollMarginBottom: 320 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Fitness Level */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{t('onboarding.step2.title')}</h3>
              </div>
              
              <div className="grid gap-2">
                {fitnessLevels.map((level) => (
                  <Card 
                    key={level.value}
                    className={`cursor-pointer transition-all border-2 ${
                      formData.fitness_level === level.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border/50 hover:border-primary/50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, fitness_level: level.value }))}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex items-start gap-2">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 mt-0.5 ${
                          formData.fitness_level === level.value 
                            ? 'border-primary bg-primary' 
                            : 'border-muted'
                        }`} />
                        <div>
                          <h4 className="text-sm font-semibold">{level.label}</h4>
                          <p className="text-xs text-muted-foreground">{level.desc}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="experience_years">{t('onboarding.step2.experienceYears')}</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="experience_years"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min={0}
                    max={50}
                    value={formData.experience_years ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d+$/.test(v)) {
                        setFormData(prev => ({ ...prev, experience_years: v === '' ? null : parseInt(v, 10) }));
                      }
                    }}
                    onFocus={(e) => {
                      e.target.select();
                      setTimeout(() => {
                        (e.target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 350);
                    }}
                    placeholder={t('onboarding.step2.experiencePlaceholder')}
                    className="pl-10"
                    style={{ scrollMarginBottom: 320 }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Overall Goals */}
          {currentStep === 3 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{t('onboarding.step3.title')}</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                {t('onboarding.step3.subtitle')}
              </p>
              
              <div className="grid grid-cols-2 gap-2">
                {primaryGoals.map((goal) => (
                  <Card
                    key={goal}
                    className={`cursor-pointer transition-all border-2 ${
                      formData.primary_goals.includes(goal)
                        ? 'border-primary bg-primary/5'
                        : 'border-border/50 hover:border-primary/50'
                    }`}
                    onClick={() => handleGoalToggle(goal)}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={formData.primary_goals.includes(goal)}
                          onCheckedChange={() => handleGoalToggle(goal)}
                          className="pointer-events-none h-4 w-4"
                        />
                        <span className="text-sm font-medium">{t(`onboarding.step3.goals.${goal}` as const, { defaultValue: goal })}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {validationErrors.primary_goals && (
                <p className="text-sm text-destructive">{validationErrors.primary_goals}</p>
              )}
            </div>
          )}

          {/* Step 4: Specific Goals */}
          {currentStep === 4 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{t('onboarding.step4.title')}</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                {t('onboarding.step4.subtitle')}
              </p>

              {onboardingGoals.length > 0 && (
                <div className="space-y-2 mb-4">
                  {onboardingGoals.map((goal, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="font-medium">{goal.exercise_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {t('onboarding.step4.targetLabel', { value: goal.target_value, unit: goal.target_unit })}
                          {goal.target_date && ` ${t('onboarding.step4.byDate', { date: new Date(goal.target_date).toLocaleDateString() })}`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveGoal(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {!showGoalDialog && (
                <Button
                  variant="outline"
                  onClick={() => setShowGoalDialog(true)}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('onboarding.step4.addGoal')}
                </Button>
              )}

              {showGoalDialog && (
                <Card className="border-primary/50">
                  <CardContent className="pt-6 space-y-4">
                    <div className="space-y-2">
                      <Label>{t('onboarding.step4.goalType')}</Label>
                      <div className="px-3 py-2 bg-muted/50 rounded-md text-sm font-medium">
                        {t('onboarding.step4.strengthGoal')}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('onboarding.step4.exerciseName')}</Label>
                      <Input
                        value={newGoal.exercise_name}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, exercise_name: e.target.value }))}
                        placeholder={t('onboarding.step4.exerciseNamePlaceholder')}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('onboarding.step4.targetValue')}</Label>
                        <Input
                          type="number"
                          value={newGoal.target_value || ''}
                          onChange={(e) => setNewGoal(prev => ({ ...prev, target_value: parseFloat(e.target.value) || 0 }))}
                          placeholder="100"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('onboarding.step4.unit')}</Label>
                        <Select
                          value={newGoal.target_unit}
                          onValueChange={(value) => setNewGoal(prev => ({ ...prev, target_unit: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="lbs">lbs</SelectItem>
                            <SelectItem value="reps">reps</SelectItem>
                            <SelectItem value="minutes">minutes</SelectItem>
                            <SelectItem value="km">km</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('onboarding.step4.currentValueOptional')}</Label>
                      <Input
                        type="number"
                        value={newGoal.current_value || ''}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, current_value: parseFloat(e.target.value) || 0 }))}
                        placeholder="80"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>{t('onboarding.step4.targetDateOptional')}</Label>
                      <Input
                        type="date"
                        value={newGoal.target_date}
                        onChange={(e) => setNewGoal(prev => ({ ...prev, target_date: e.target.value }))}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={handleAddGoal} className="flex-1">
                        {t('onboarding.step4.addGoalButton')}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowGoalDialog(false);
                          setNewGoal({
                            goal_type: 'strength',
                            exercise_name: '',
                            target_value: 0,
                            target_unit: 'kg',
                            current_value: 0,
                            starting_value: null,
                            target_date: '',
                            is_active: true,
                            description: ''
                          });
                        }}
                      >
                        {t('onboarding.step4.cancel')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {onboardingGoals.length === 0 && !showGoalDialog && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('onboarding.step4.noGoalsYet')}
                </p>
              )}
            </div>
          )}

          {/* Step 5: Work Type */}
          {currentStep === 5 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{t('onboarding.step5.title')}</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-2">{t('onboarding.step5.subtitle')}</p>

              <div className="space-y-2">
                {[
                  { value: 'student', label: t('onboarding.step5.workTypes.student') },
                  { value: 'office', label: t('onboarding.step5.workTypes.office') },
                  { value: 'physical', label: t('onboarding.step5.workTypes.physical') },
                  { value: 'remote', label: t('onboarding.step5.workTypes.remote') },
                  { value: 'other', label: t('onboarding.step5.workTypes.other') },
                ].map((type) => (
                  <Card 
                    key={type.value}
                    className={`cursor-pointer transition-all border-2 ${
                      formData.work_type === type.value 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border/50 hover:border-primary/50'
                    }`}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, work_type: type.value }));
                      // Clear error when user selects
                      if (validationErrors.work_type) {
                        setValidationErrors(prev => {
                          const next = { ...prev };
                          delete next.work_type;
                          return next;
                        });
                      }
                    }}
                  >
                    <CardContent className="p-2.5">
                      <div className="flex items-center gap-2">
                        <div className={`w-3.5 h-3.5 rounded-full border-2 ${
                          formData.work_type === type.value 
                            ? 'border-primary bg-primary' 
                            : 'border-muted'
                        }`} />
                        <span className="text-sm font-medium">{type.label}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {formData.work_type === 'other' && (
                  <div className="space-y-2 pt-2">
                    <Label htmlFor="work_type_custom">{t('onboarding.step5.specifyWorkLabel')}</Label>
                    <Input
                      id="work_type_custom"
                      value={formData.work_type_custom}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, work_type_custom: e.target.value }));
                        // Clear error when user types
                        if (validationErrors.work_type_custom) {
                          setValidationErrors(prev => {
                            const next = { ...prev };
                            delete next.work_type_custom;
                            return next;
                          });
                        }
                      }}
                      placeholder={t('onboarding.step5.specifyWorkPlaceholder')}
                      className={validationErrors.work_type_custom ? 'border-destructive' : ''}
                    />
                    {validationErrors.work_type_custom && (
                      <p className="text-sm text-destructive">{validationErrors.work_type_custom}</p>
                    )}
                  </div>
                )}
              </div>
              {validationErrors.work_type && (
                <p className="text-sm text-destructive">{validationErrors.work_type}</p>
              )}
            </div>
          )}

          {/* Step 6: Connect Apple Health (iOS only) - non-iOS skips to Health Conditions */}
          {currentStep === 6 && isIOS && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <Watch className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold">{t('onboarding.step6.title')}</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {t('onboarding.step6.subtitle')}
              </p>
              {healthConnected ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-primary/10 border border-primary/30">
                  <CheckCircle2 className="h-8 w-8 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">{t('onboarding.step6.connected')}</p>
                    <p className="text-sm text-muted-foreground">{t('onboarding.step6.syncDescription')}</p>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleConnectAppleHealth}
                  disabled={isConnectingHealth}
                  className="w-full gap-2 bg-gradient-primary text-black shadow-glow hover:shadow-lg h-12"
                  size="lg"
                >
                  {isConnectingHealth ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('onboarding.connecting')}
                    </>
                  ) : (
                    <>
                      <Activity className="h-5 w-5" />
                      {t('onboarding.step6.connectButton')}
                    </>
                  )}
                </Button>
              )}
              <p className="text-xs text-muted-foreground text-center">
                {t('onboarding.step6.skipNote')}
              </p>
            </div>
          )}

          {/* Step 7 (iOS) or Step 6 (non-iOS): Health Deficiencies */}
          {((currentStep === 6 && !isIOS) || (currentStep === 7 && isIOS)) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <User className="h-5 w-5 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">{t('onboarding.step7.title')}</h3>
                  <p className="text-xs text-muted-foreground">{t('onboarding.step7.subtitle')}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="health_deficiencies">{t('onboarding.step7.label')}</Label>
                <Textarea
                  id="health_deficiencies"
                  value={formData.health_deficiencies}
                  onChange={(e) => setFormData(prev => ({ ...prev, health_deficiencies: e.target.value }))}
                  placeholder={t('onboarding.step7.placeholder')}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  {t('onboarding.step7.privacy')}
                </p>
              </div>
            </div>
          )}

          {/* Step 8 (iOS) or Step 7 (non-iOS): Nutrition Restrictions */}
          {((currentStep === 7 && !isIOS) || (currentStep === 8 && isIOS)) && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-5 w-5 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">{t('onboarding.step8.title')}</h3>
                  <p className="text-xs text-muted-foreground">{t('onboarding.step8.subtitle')}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="nutrition_restrictions">{t('onboarding.step8.label')}</Label>
                <Textarea
                  id="nutrition_restrictions"
                  value={formData.nutrition_restrictions}
                  onChange={(e) => setFormData(prev => ({ ...prev, nutrition_restrictions: e.target.value }))}
                  placeholder={t('onboarding.step8.placeholder')}
                  rows={3}
                />
              </div>

              {/* Data Collection Consent */}
              <div className={`mt-4 p-3 bg-muted/50 rounded-lg border ${validationErrors.dataConsent ? 'border-destructive' : 'border-border/50'}`}>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="data-consent"
                    checked={dataConsentAccepted}
                    onCheckedChange={(checked) => {
                      setDataConsentAccepted(checked === true);
                      // Clear error when user accepts
                      if (validationErrors.dataConsent) {
                        setValidationErrors(prev => {
                          const next = { ...prev };
                          delete next.dataConsent;
                          return next;
                        });
                      }
                    }}
                    className="mt-0.5"
                  />
                  <Label htmlFor="data-consent" className="text-sm leading-relaxed cursor-pointer">
                    {t('onboarding.step8.consent')}{' '}
                    <Dialog>
                      <DialogTrigger asChild>
                        <span className="text-primary underline cursor-pointer">{t('onboarding.step8.privacyPolicy')}</span>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg max-h-[80vh]">
                        <DialogHeader>
                          <DialogTitle>{t('onboarding.step8.privacyPolicyDialogTitle')}</DialogTitle>
                        </DialogHeader>
                        <ScrollArea className="h-[60vh] pr-4">
                          <PrivacyPolicyContent compact />
                        </ScrollArea>
                      </DialogContent>
                    </Dialog>.
                  </Label>
                </div>
              </div>
              {validationErrors.dataConsent && (
                <p className="text-sm text-destructive mt-2">{validationErrors.dataConsent}</p>
              )}
            </div>
          )}

        </CardContent>

        {/* Navigation - Fixed at bottom of card */}
        <div className="flex justify-between p-4 pt-3 border-t border-border/50 flex-shrink-0">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1 || isSubmitting}
            className="flex items-center gap-2"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('onboarding.previous')}
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed() || isSubmitting}
              className="flex items-center gap-2"
              size="sm"
            >
              {t('onboarding.next')}
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
              className="flex items-center gap-2 bg-gradient-primary text-black border-0"
              size="sm"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('onboarding.creating')}
                </>
              ) : (
                <>
                  {t('onboarding.completeSetup')}
                  <Zap className="h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};